'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { products } from '@/data/products';

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={filled ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'} aria-hidden="true">
    <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
  </svg>
);

const ZapIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="fill-[#1c1c1c]" aria-hidden="true">
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
  </svg>
);

const CartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="8" cy="21" r="1" />
    <circle cx="19" cy="21" r="1" />
    <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
  </svg>
);

const filters = [
  { label: 'All', icon: 'flame' },
  { label: 'Best Sellers', icon: 'trending-up' },
  { label: 'Top Rated', icon: 'star' },
  { label: 'Biggest Deals', icon: 'tag' },
];

export default function ProductsSection() {
  const [activeFilter, setActiveFilter] = useState('All');

  return (
    <section className="bg-gray-50 pb-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-500 fill-red-500" aria-hidden="true">
                <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
              </svg>
              <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 tracking-tight">Today&apos;s Best Deals</h2>
            </div>
            <p className="text-sm text-gray-500">14 products &nbsp;·&nbsp; Fresh combos, daily essentials &amp; more</p>
          </div>

          {/* Filter Buttons */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 shrink-0" aria-hidden="true">
              <path d="M10 5H3" /><path d="M12 19H3" /><path d="M14 3v4" /><path d="M16 17v4" />
              <path d="M21 12h-9" /><path d="M21 19h-5" /><path d="M21 5h-7" />
              <path d="M8 10v4" /><path d="M8 12H3" />
            </svg>
            {filters.map((f) => (
              <button
                key={f.label}
                onClick={() => setActiveFilter(f.label)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold shrink-0 border transition-all duration-150 ${
                  activeFilter === f.label
                    ? 'bg-[#0c831f] text-white border-[#0c831f] shadow-sm'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-[#0c831f] hover:text-[#0c831f]'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
          {products.map((product) => (
            <Link
              key={product.id}
              href={`/product-details/${product.id}`}
              className="group bg-white rounded-2xl border border-gray-200 hover:border-[#0c831f] hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col"
            >
              {/* Image */}
              <div className="relative aspect-square bg-gray-50 overflow-hidden">
                <div className="object-contain m-3 relative">
                  <img
                    alt={product.name}
                    className="w-full h-full"
                    src={product.image}
                  />
                </div>
                <span className="absolute top-2.5 left-2.5 bg-red-500 text-white text-[11px] font-extrabold px-2 py-0.5 rounded-md shadow-sm">
                  {product.discount}% Off
                </span>
                <span className="absolute bottom-3.5 right-3.5 bg-[#f8c42a] text-[#1c1c1c] text-[10px] font-extrabold px-2 py-0.5 rounded-md flex items-center gap-1">
                  <ZapIcon /> 4 hours
                </span>
              </div>

              {/* Info */}
              <div className="p-3 md:p-4 flex flex-col gap-2 flex-1">
                <p className="text-xs md:text-sm font-semibold text-gray-800 line-clamp-2 leading-snug">
                  {product.name}
                </p>

                {/* Stars */}
                <div className="flex items-center gap-1.5">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIcon key={star} filled={star <= product.rating} />
                    ))}
                  </div>
                  <span className="text-[10px] text-gray-400 font-medium">({product.reviews})</span>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-base md:text-lg font-extrabold text-gray-900">₹{product.price}</span>
                  <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                  <span className="text-[10px] text-green-700 font-bold bg-green-50 px-1.5 py-0.5 rounded">{product.discount}% Off</span>
                </div>

                <p className="text-[10px] text-gray-400 flex items-center gap-1">
                  🚚 <span>Free delivery</span>
                </p>

                <button className="mt-auto w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 active:scale-95 border-2 border-[#0c831f] text-[#0c831f] hover:bg-[#0c831f] hover:text-white">
                  <CartIcon />
                  Add to cart
                </button>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
}
